import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { WarningModal } from "../../components/Modal";
import Icon from "../../components/Icon";
import {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
} from "./redux/actions";
import { SEND_STATUS_LABEL } from "./api";
import "./style.scss";

const STATUS_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "SENT", label: "Sent" },
  { value: "DRAFT", label: "Draft" },
  { value: "FAILED", label: "Failed" }
];

const PAGE_SIZE = 20;

const CONFIRM_TITLE = {
  SEND: notification =>
    `Send "${notification.header}" to the ${notification.userScope} customer group now?`,
  DELETE: notification => `Delete "${notification.header}"?`,
  // A delivered campaign is hidden rather than deleted: the customers who already have it
  // keep it out of sight, and its Reach / View history stays.
  DEACTIVATE: notification =>
    `Hide "${notification.header}" from customers' inbox? (Reach / View totals are kept)`,
  ACTIVATE: notification => `Show "${notification.header}" in the app again?`
};

const formatDate = value => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleString("th-TH");
};

/*
 * A campaign that has gone out can no longer be edited or deleted; it is history.
 *
 * The test is the send time, not the send status: a campaign whose pushes all failed
 * still reached every recipient's inbox and may already have been read. This mirrors
 * the backend guard, which refuses the edit either way.
 */
const isDelivered = notification => Boolean(notification.timeSent);

function NotificationList(props) {
  const {
    REQNotificationList,
    SENDxNotification,
    DELxNotification,
    SETxNotificationStatus,
    CleanUpNotificationList,
    REQStrNotificationList,
    NotificationList,
    Pagination,
    errorMessage
  } = props;


  const [oSearch, SETxSearch] = useState("");
  const [oSubmittedSearch, SETxSubmittedSearch] = useState("");
  const [oFilter, SETxFilter] = useState("ALL");
  const [oPage, SETxPage] = useState(1);
  const [oConfirm, SETxConfirm] = useState(null);
  const [oBrokenImages, SETxBrokenImages] = useState({});

  const filters = {
    search: oSubmittedSearch,
    sendStatus: oFilter,
    page: oPage,
    limit: PAGE_SIZE
  };

  useEffect(() => {
    REQNotificationList(filters);
    return () => {
      CleanUpNotificationList();
    };
  }, [oSubmittedSearch, oFilter, oPage]);

  const onSearch = event => {
    event.preventDefault();
    SETxPage(1);
    SETxSubmittedSearch(oSearch.trim());
  };

  const onConfirm = () => {
    if (!oConfirm) {
      return;
    }
    if (oConfirm.action === "SEND") {
      SENDxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DELETE") {
      DELxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DEACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "ACTIVATE") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    SETxConfirm(null);
  };

  const totalPages = (Pagination && Pagination.totalPages) || 0;

  return (
    <Fragment>
      {REQStrNotificationList ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading notifications...</p>
        </div>
      ) : null}

      <div className="noti-list-container">
        <div className="noti-list-topbar">
          <div className="noti-list-titles">
            <h2 className="noti-list-heading">Notification List</h2>
            <p className="noti-list-subheading">
              Notifications sent to customers in the Principal TH app — create, edit, and track results
            </p>
          </div>

          <div className="noti-list-actions">
            <form className="noti-list-search" onSubmit={onSearch}>
              <input
                type="text"
                placeholder="Search"
                value={oSearch}
                onChange={event => SETxSearch(event.target.value)}
              />
              <button type="submit">
                <Icon name="search" />
                Search
              </button>
            </form>
            <Link className="noti-list-create" to="/PushNoti_Compose">
              <Icon name="plus" size={18} />
              Create new
            </Link>
          </div>
        </div>

        {errorMessage ? <div className="noti-list-error">{errorMessage}</div> : null}

        <div className="noti-list-filters">
          {STATUS_FILTERS.map(filter => (
            <button
              type="button"
              key={filter.value}
              className={oFilter === filter.value ? "noti-list-filter active" : "noti-list-filter"}
              onClick={() => {
                SETxPage(1);
                SETxFilter(filter.value);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="noti-list-rows">
          {(NotificationList || []).map(notification => (
            <div className="noti-list-row" key={notification.id}>
              <div className="noti-list-code">{notification.code}</div>

              <div className="noti-list-thumb">
                {notification.imageUrl && !oBrokenImages[notification.id] ? (
                  <img
                    src={notification.imageUrl}
                    alt=""
                    // A URL that no longer resolves would otherwise leave a broken-image
                    // glyph in the row; fall back to the content-type dot instead.
                    onError={() => SETxBrokenImages(current => ({ ...current, [notification.id]: true }))}
                  />
                ) : (
                  <span className={`noti-list-dot ${notification.contentType.toLowerCase()}`} />
                )}
              </div>

              <div className="noti-list-content">
                <p className="noti-list-title">{notification.header}</p>
                <p className="noti-list-details">{notification.content}</p>
                <p className="noti-list-meta">
                  {notification.name} · {notification.contentType} · {notification.notificationType} ·{" "}
                  {notification.userScope}
                </p>
              </div>

              <div className="noti-list-cell">
                <span className="noti-list-cell-label">Time sent</span>
                {formatDate(notification.timeSent || notification.scheduledAt)}
              </div>

              <div className="noti-list-cell">
                <span className="noti-list-cell-label">Reach</span>
                {notification.reach}
              </div>

              <div className="noti-list-cell">
                <span className="noti-list-cell-label">View</span>
                {notification.view}
              </div>

              <div className={`noti-list-status ${notification.sendStatus.toLowerCase()}`}>
                {SEND_STATUS_LABEL[notification.sendStatus] || notification.sendStatus}
              </div>

              <div className="noti-list-row-actions">
                {isDelivered(notification) ? (
                  <Fragment>
                    <Link className="noti-list-edit ghost" to={`/PushNoti_Compose?id=${notification.id}`}>
                      <Icon name="eye" />
                      View
                    </Link>
                    {/* Sent already: it cannot be edited or deleted, only hidden from the app */}
                    <button
                      type="button"
                      className={notification.status === "ACTIVE" ? "noti-list-delete" : "noti-list-send"}
                      onClick={() =>
                        SETxConfirm({
                          action: notification.status === "ACTIVE" ? "DEACTIVATE" : "ACTIVATE",
                          notification
                        })
                      }>
                      <Icon name={notification.status === "ACTIVE" ? "eyeOff" : "eye"} />
                      {notification.status === "ACTIVE" ? "Hide" : "Show"}
                    </button>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Link className="noti-list-edit" to={`/PushNoti_Compose?id=${notification.id}`}>
                      <Icon name="edit" />
                      Edit
                    </Link>
                    {notification.status === "ACTIVE" ? (
                      <button
                        type="button"
                        className="noti-list-send"
                        onClick={() => SETxConfirm({ action: "SEND", notification })}
                      >
                        <Icon name="send" />
                        Send now
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="noti-list-delete"
                      onClick={() => SETxConfirm({ action: "DELETE", notification })}
                    >
                      <Icon name="trash" />
                      Delete
                    </button>
                  </Fragment>
                )}
              </div>
            </div>
          ))}

          {!REQStrNotificationList && (NotificationList || []).length === 0 ? (
            <div className="noti-list-empty">No notifications yet</div>
          ) : null}
        </div>

        {totalPages > 1 ? (
          <div className="noti-list-pager">
            <button type="button" disabled={oPage <= 1} onClick={() => SETxPage(oPage - 1)}>
              Previous
            </button>
            <span>
              Page {Pagination.page} / {totalPages} ({Pagination.total} total)
            </span>
            <button type="button" disabled={oPage >= totalPages} onClick={() => SETxPage(oPage + 1)}>
              Next
            </button>
          </div>
        ) : null}
      </div>

      <WarningModal
        show={oConfirm !== null}
        onHide={() => SETxConfirm(null)}
        onHandleGo={onConfirm}
        onHandleCancel={() => SETxConfirm(null)}
        type={"Disapprove"}
        status={"1"}
        title={oConfirm ? CONFIRM_TITLE[oConfirm.action](oConfirm.notification) : ""}
      />
    </Fragment>
  );
}

const mapStateToProps = state => ({
  REQStrNotificationList: state.notificationListReducer.REQStrNotificationList,
  NotificationList: state.notificationListReducer.NotificationList,
  Pagination: state.notificationListReducer.Pagination,
  errorMessage: state.notificationListReducer.errorMessage
});

const mapDispatchToProps = {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NotificationList);
